import { type ActionReducerMapBuilder, isAnyOf } from '@reduxjs/toolkit';
import { isNotDefined } from '@sgme/fp';

import { callOrderApi } from '@/store/orders/call/api/index.ts';
import { isCallOrder } from '@/store/orders/call/helpers.ts';
import type { CallOrder } from '@/store/orders/call/state.ts';
import { isSubmitOrderResponse, type OrderById } from '@/store/orders/state.ts';

// ██████╗ ███████╗███╗   ██╗██████╗ ██╗███╗   ██╗ ██████╗
// ██╔══██╗██╔════╝████╗  ██║██╔══██╗██║████╗  ██║██╔════╝
// ██████╔╝█████╗  ██╔██╗ ██║██║  ██║██║██╔██╗ ██║██║  ███╗
// ██╔═══╝ ██╔══╝  ██║╚██╗██║██║  ██║██║██║╚██╗██║██║   ██║
// ██║     ███████╗██║ ╚████║██████╔╝██║██║ ╚████║╚██████╔╝
// ╚═╝     ╚══════╝╚═╝  ╚═══╝╚═════╝ ╚═╝╚═╝  ╚═══╝ ╚═════╝

export const submitPending = (builder: ActionReducerMapBuilder<OrderById>) =>
  builder.addMatcher(
    isAnyOf(callOrderApi.endpoints.updateCallOrder.matchPending, callOrderApi.endpoints.submitCallOrder.matchPending),
    (state, action) => {
      const tileId = action.meta.arg.originalArgs.tileId;
      const currentOrder = state[tileId];
      if (isNotDefined(currentOrder) || !isCallOrder(currentOrder)) {
        return state;
      }

      state[tileId] = {
        ...currentOrder,
        properties: {
          ...currentOrder.properties,
          status: {
            type: 'pending',
          },
        },
      };

      return state; // for sonar
    },
  );

// ███████╗██╗   ██╗██╗     ██╗     ███████╗██╗██╗     ██╗     ███████╗██████╗
// ██╔════╝██║   ██║██║     ██║     ██╔════╝██║██║     ██║     ██╔════╝██╔══██╗
// █████╗  ██║   ██║██║     ██║     █████╗  ██║██║     ██║     █████╗  ██║  ██║
// ██╔══╝  ██║   ██║██║     ██║     ██╔══╝  ██║██║     ██║     ██╔══╝  ██║  ██║
// ██║     ╚██████╔╝███████╗███████╗██║     ██║███████╗███████╗███████╗██████╔╝
// ╚═╝      ╚═════╝ ╚══════╝╚══════╝╚═╝     ╚═╝╚══════╝╚══════╝╚══════╝╚═════╝

export const submitFulfilled = (builder: ActionReducerMapBuilder<OrderById>) =>
  builder.addMatcher(
    isAnyOf(
      callOrderApi.endpoints.updateCallOrder.matchFulfilled,
      callOrderApi.endpoints.submitCallOrder.matchFulfilled,
    ),
    (state, action) => {
      const tileId = action.meta.arg.originalArgs.tileId;
      const currentOrder = state[tileId];
      if (isNotDefined(currentOrder) || !isCallOrder(currentOrder) || action.payload.type === 'failure') {
        return state;
      }

      state[tileId] = {
        ...currentOrder,
        properties: {
          ...currentOrder.properties,
          status: {
            type: 'submitted',
            tradeId: action.payload.orderId,
          },
          isReadyToSubmit: false,
        },
      };

      return state; // for sonar
    },
  );

// ██████╗ ███████╗     ██╗███████╗ ██████╗████████╗███████╗██████╗
// ██╔══██╗██╔════╝     ██║██╔════╝██╔════╝╚══██╔══╝██╔════╝██╔══██╗
// ██████╔╝█████╗       ██║█████╗  ██║        ██║   █████╗  ██║  ██║
// ██╔══██╗██╔══╝  ██   ██║██╔══╝  ██║        ██║   ██╔══╝  ██║  ██║
// ██║  ██║███████╗╚█████╔╝███████╗╚██████╗   ██║   ███████╗██████╔╝
// ╚═╝  ╚═╝╚══════╝ ╚════╝ ╚══════╝ ╚═════╝   ╚═╝   ╚══════╝╚═════╝

export const submitRejected = (builder: ActionReducerMapBuilder<OrderById>) =>
  builder.addMatcher(
    isAnyOf(callOrderApi.endpoints.updateCallOrder.matchRejected, callOrderApi.endpoints.submitCallOrder.matchRejected),
    (state, action) => {
      const tileId = action.meta.arg.originalArgs.tileId;

      const currentOrder = state[tileId];
      const payload = isSubmitOrderResponse(action.payload) ? action.payload : undefined;

      if (
        isNotDefined(currentOrder) ||
        isNotDefined(payload) ||
        currentOrder.type !== 'CallOrder' ||
        payload.type !== 'failure'
      ) {
        return state;
      }

      const errorMessage = (() => {
        if (payload.errors.some((error) => error.errorDescription === 'timeout')) {
          return 'order.timeout';
        }
        if (payload.errors.some((error) => error.errorDescription === 'server.error')) {
          return 'order.serverError';
        }
        if (payload.errors.some((error) => error.errorDescription === 'order.unexpected')) {
          return 'order.unexpected';
        }
        return 'order.validationFailed';
      })();

      const areErrorsKnown = payload.errors.some((error) => error.field !== 'unknown');

      const errors = payload.errors.reduce(
        (acc, error) => {
          if (error.field === 'amountInCcy1' || error.field === 'amountInCcy2') {
            acc.notional = error;
          }

          if (error.field === 'base64Emails') {
            acc.notificationEmails = error;
          }

          acc[error.field] = error;
          return acc;
        },
        {} as Record<string, { errorCode: number; errorDescription: string; field: string }>,
      );

      const actionType = action.meta.arg.endpointName;

      state[tileId] = {
        ...currentOrder,
        type: 'CallOrder',
        properties: {
          ...currentOrder.properties,
          status: areErrorsKnown
            ? { type: actionType === 'submitCallOrder' ? 'creating' : 'submitted' }
            : { type: 'failed', errorMessage },
          errors: areErrorsKnown ? errors : {},
        },
      } as CallOrder;

      return state; // for sonar
    },
  );
